import React, {createContext, Fragment, useCallback, useContext, useRef, useState} from "react";
import {NotificationInstance} from "antd/es/notification/interface";
import {notification} from "antd";
import {usePusher} from "@/base/utils/pusher.provider";
import {useUser} from "@/base/components/auth/UserProvider";
import {NotificationData} from "@/base/types/NotificationData";
import {UserData} from "@/base/types/UserData";
import {NotificationOutlinedIcon} from "@/base/components/icon/NotificationOutlinedIcon";
import {DelayedEffectHook} from "@/base/types/delayed-effect.hook";

type Context = {
	api: NotificationInstance;
	updateNotification: number;
}

const NotificationContext = createContext({} as Context)

export const NotificationProvider = ({children}: { children: React.ReactNode }) => {
	const audioPlayer = useRef(null);
	const [loadNotificationSound, setLoadNotificationSound] = useState(false);

	const playNotificationSound = useCallback(async () => {
		console.debug(loadNotificationSound, audioPlayer.current);
		if (!loadNotificationSound && !audioPlayer.current)
		{
			setLoadNotificationSound(true);
			return;
		}

		if (audioPlayer.current)
		{
			// @ts-ignore
			audioPlayer.current.play();
		}
	}, [audioPlayer, loadNotificationSound]);

	DelayedEffectHook(() => {
		if (audioPlayer.current)
		{
			playNotificationSound().then(r => null);
		}
	}, [audioPlayer.current], 100);

	const [api, contextHolder] = notification.useNotification({
		top: 100,
	});

	const [updateNotification, setUpdateNotification] = useState(0);

	const UserContext = useUser();
	const {pusher, isUserConnected, isConnected, onPersonalEvent} = usePusher();

	DelayedEffectHook(() => {
		if (isUserConnected())
		{
			return onPersonalEvent('notification', (data: { user: UserData, notification: NotificationData }) => {
				const isTabActive = document.visibilityState === 'visible';

				api.info({
					message: <p className={`font-medium font-montserrat-alternates`}>{data.notification.title}</p>,
					description: data.notification.message,
					icon: <NotificationOutlinedIcon width={30} height={30}/>,
					duration: isTabActive ? 5 : 20
				})

				// data.notification.created_at
				playNotificationSound().then(r => {
				});

				setUpdateNotification(Math.random());
			});
		}

		if (process.env.NEXT_PUBLIC_NODE_ENV != 'production')
		{
			const handleKeyDown = async (event: KeyboardEvent) => {
				console.debug(event.key, event.shiftKey);
				if (event.key.toLowerCase() === 'p' && event.shiftKey)
				{
					await playNotificationSound();
				}
			}

			document.addEventListener('keydown', handleKeyDown);

			return () => {
				document.removeEventListener('keydown', handleKeyDown);
			}
		}
	}, [pusher, UserContext.user], 100);

	const context: Context = {api, updateNotification}

	return <NotificationContext.Provider value={context}>
		{contextHolder}
		{loadNotificationSound && <audio onLoad={() => setTimeout(playNotificationSound, 200)} ref={audioPlayer} src={`/sound/notification.ogg`}/>}
		<Fragment>
			{children}
		</Fragment>
	</NotificationContext.Provider>
}

export const useNotification = () => {
	const context = useContext(NotificationContext);
	return [context.api, context] as [Context['api'], Context];
}